<script setup lang="ts">
import {
  CategoryScale,
  type Chart,
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Filler,
  LinearScale,
  LineElement,
  type Point,
  PointElement,
  type ScriptableContext,
  type ScriptableLineSegmentContext,
  TimeScale,
  Tooltip,
  type TooltipModel,
} from 'chart.js'
import { computed, ref } from 'vue'
import { Line as LineVueChart } from 'vue-chartjs'

import { diffDates, formatDateShort } from '../../formatters/date.formatter'
import { hexToRgba } from '../../utils/color.utils'
import PlatformIconComponent from '../atoms/PlatformIconComponent.vue'
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm'

type TooltipBreakdownEntry = { platform: string; label: string; formatted: string | number }
type ChartDataset = { x: string; y: string | number; longAmount: string | number; valueUnit?: string; breakdown?: TooltipBreakdownEntry[] }

ChartJS.register(Tooltip, LineElement, CategoryScale, LinearScale, TimeScale, Filler, PointElement)

type TooltipAnchor = 'leading' | 'trailing' | 'center'

type TooltipState = {
  visible: boolean
  x: number
  y: number
  title: string
  value: string | number
  valueUnit: string
  breakdown: TooltipBreakdownEntry[]
  anchor: TooltipAnchor
}

const tooltipState = ref<TooltipState>({
  visible: false,
  x: 0,
  y: 0,
  title: '',
  value: '',
  valueUnit: '',
  breakdown: [],
  anchor: 'center',
})

const props = withDefaults(
  defineProps<{
    data: ChartDataset[]
    customIntervalUnit?: string
    positiveColor?: string
    negativeColor?: string
    formatYAxisLabel?: (value: number) => string
  }>(),
  {
    data: () => [],
    positiveColor: '#349FD6',
    negativeColor: '#d33131',
  },
)
const positiveLightColor = computed(() => hexToRgba(props.positiveColor, 0.32))
const negativeLightColor = computed(() => hexToRgba(props.negativeColor, 0.32))
const chooseColor = (value: number, positiveColor: string, negativeColor: string): string | null => {
  return value >= 0 ? positiveColor : negativeColor
}

const data = computed(() => ({
  datasets: [
    {
      data: props.data,
      fill: 'origin',
      borderWidth: 1,
      backgroundColor: (context: { chart: Chart }) => {
        const chart = context.chart
        const { ctx, chartArea } = chart

        if (!chartArea) {
          return null // Avoid issues during chart resizing
        }

        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        const zeroPixel = chart.scales.y!.getPixelForValue(0)
        const chartHeight = chartArea.bottom - chartArea.top
        const zeroOffset = chartHeight === 0 ? 0 : Math.min(1, Math.max(0, (zeroPixel - chartArea.top) / chartHeight))
        gradient.addColorStop(0, positiveLightColor.value)
        gradient.addColorStop(zeroOffset, hexToRgba(props.positiveColor, 0))
        gradient.addColorStop(zeroOffset, hexToRgba(props.negativeColor, 0))
        gradient.addColorStop(1, negativeLightColor.value)

        return gradient
      },
      segment: {
        borderColor: (context: ScriptableLineSegmentContext) => {
          const { p0, p1 } = context
          const y0 = p0.parsed.y ?? 0
          const y1 = p1.parsed.y ?? 0

          if (y0 > 0 && y1 > 0) {
            return props.positiveColor
          } else if (y0 < 0 && y1 < 0) {
            return props.negativeColor
          }

          return props.positiveColor
        },
      },
      pointRadius: ({ parsed }: ScriptableContext<'line'>) => ((parsed?.y ?? 0) !== 0 ? 3 : 0.1),
      pointBorderColor: ({ parsed }: ScriptableContext<'line'>) => chooseColor(parsed?.y ?? 0, props.positiveColor, props.negativeColor),
      pointBackgroundColor: ({ parsed }: ScriptableContext<'line'>) =>
        chooseColor(parsed?.y ?? 0, props.positiveColor, props.negativeColor),
      pointHoverBorderColor: ({ parsed }: ScriptableContext<'line'>) =>
        chooseColor(parsed?.y ?? 0, positiveLightColor.value, negativeLightColor.value),
      pointHoverBackgroundColor: ({ parsed }: ScriptableContext<'line'>) =>
        chooseColor(parsed?.y ?? 0, props.positiveColor, props.negativeColor),
      pointHoverBorderWidth: 10,
      pointHoverRadius: 3,
    },
  ],
})) as unknown as ChartData<'line', Point[]>

const intervalUnit = computed(() => {
  if (props.customIntervalUnit) {
    return props.customIntervalUnit
  }

  const length = props.data.length
  if (length <= 0) {
    return 'day'
  }

  if (diffDates(props.data[length - 1]!.x, props.data[0]!.x, 'month') > 6) {
    return 'month'
  } else if (diffDates(props.data[length - 1]!.x, props.data[0]!.x, 'day') > 45) {
    return 'week'
  }

  return 'day'
})

const options = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'time',
      time: {
        unit: intervalUnit.value,
        isoWeekday: true,
        round: true,
        displayFormats: {
          day: 'MMM D',
          week: 'MMM D',
          month: 'MMM',
        },
      },
    },
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value: number) => (props.formatYAxisLabel ? props.formatYAxisLabel(value) : value),
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
      external: (context: { chart: Chart; tooltip: TooltipModel<'line'> }) => {
        const { tooltip } = context
        if (tooltip.opacity === 0) {
          tooltipState.value = { ...tooltipState.value, visible: false }
          return
        }
        const point = tooltip.dataPoints?.[0]
        if (!point) {
          return
        }
        const raw = point.raw as ChartDataset
        const lastIndex = props.data.length - 1
        const isFirstPoint = point.dataIndex === 0
        const isLastPoint = point.dataIndex === lastIndex
        // With a single point there's no meaningful "first vs last" edge to avoid, so it
        // stays centered rather than picking one arbitrarily.
        const anchor: TooltipAnchor = lastIndex <= 0 ? 'center' : isFirstPoint ? 'leading' : isLastPoint ? 'trailing' : 'center'
        tooltipState.value = {
          visible: true,
          x: tooltip.caretX,
          y: tooltip.caretY,
          title: formatDateShort(raw.x),
          value: raw.longAmount,
          valueUnit: raw.valueUnit ?? '',
          breakdown: raw.breakdown ?? [],
          anchor,
        }
      },
    },
  },
})) as unknown as ChartOptions<'line'>
</script>
<template>
  <div class="line-chart-wrapper">
    <LineVueChart :data="data" :options="options" />
    <div
      v-if="tooltipState.visible"
      class="line-chart-tooltip"
      :class="{
        'line-chart-tooltip--leading': tooltipState.anchor === 'leading',
        'line-chart-tooltip--trailing': tooltipState.anchor === 'trailing',
      }"
      :style="{ left: `${tooltipState.x}px`, top: `${tooltipState.y}px` }"
    >
      <span class="line-chart-tooltip__date">{{ tooltipState.title }}</span>
      <span class="line-chart-tooltip__value"
        >{{ tooltipState.value }}{{ tooltipState.valueUnit ? ` ${tooltipState.valueUnit}` : '' }}</span
      >
      <div v-if="tooltipState.breakdown.length" class="line-chart-tooltip__breakdown">
        <div v-for="entry in tooltipState.breakdown" :key="entry.platform" class="line-chart-tooltip__breakdown-row">
          <PlatformIconComponent :platform="entry.platform" :size="18" />
          <span class="line-chart-tooltip__breakdown-label">{{ entry.label }}</span>
          <span class="line-chart-tooltip__breakdown-value">{{ entry.formatted }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.line-chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.line-chart-tooltip {
  position: absolute;
  transform: translate(-50%, calc(-100% - 8px));
  background: #ffffff;
  border-radius: 16px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px 0 rgba(60, 75, 93, 0.16);
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;

  &--leading {
    transform: translate(0, calc(-100% - 8px));
  }

  &--trailing {
    transform: translate(-100%, calc(-100% - 8px));
  }

  &__date {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #222222;
  }

  &__value {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #222222;
  }

  &__breakdown {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__breakdown-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__breakdown-label {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #222222;
  }

  &__breakdown-value {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #222222;
    margin-left: auto;
    padding-left: 8px;
  }
}
</style>
